import { useEffect, useRef } from 'react';
import type { AnimationItem } from 'lottie-web';
import { REACTIONS } from '@minigame-race/contracts';

/**
 * Lottie is ~370KB, and emotes only ever appear mid-game — so load the player
 * lazily, the first time an emote actually renders, rather than bloating first
 * paint. `lottie-web/lottie_light` is the SVG-only build (no canvas/HTML
 * renderers we don't use), which is meaningfully smaller.
 */
let lottiePromise: Promise<typeof import('lottie-web/build/player/lottie_light.js').default> | null = null;
function getLottie(): Promise<typeof import('lottie-web/build/player/lottie_light.js').default> {
  if (!lottiePromise) lottiePromise = import('lottie-web/build/player/lottie_light.js').then((m) => m.default);
  return lottiePromise;
}

/**
 * Maps each reaction emoji to its Google Noto animated-emoji Lottie file
 * (bundled under /emotes). Noto gives a consistent, characterful look across
 * every device — unlike raw system emoji, which each OS draws differently.
 *
 * The JSONs are served statically and fetched lazily (only when an emote is
 * actually shown), so they never weigh down first paint.
 */
const EMOTE_FILE: Record<(typeof REACTIONS)[number], string> = {
  '🫪': 'distorted',
  '😂': 'joy',
  '🔥': 'fire',
  '😮': 'wow',
  '😭': 'cry',
  '🧠': 'brain',
  '🎯': 'target',
  '🐢': 'turtle',
};

/** In-memory cache of parsed Lottie JSON, keyed by file name. */
const dataCache = new Map<string, unknown>();
const loading = new Map<string, Promise<unknown>>();

async function loadData(file: string): Promise<unknown> {
  const cached = dataCache.get(file);
  if (cached) return cached;
  let p = loading.get(file);
  if (!p) {
    p = fetch(`/emotes/${file}.json`)
      .then((r) => r.json())
      .then((json) => {
        dataCache.set(file, json);
        return json;
      });
    loading.set(file, p);
  }
  return p;
}

/** Warm the cache for every emote (call when the picker opens). */
export function preloadEmotes(): void {
  for (const file of Object.values(EMOTE_FILE)) void loadData(file);
}

/**
 * Renders one animated Noto emote. `loop` for the picker (idles forever), or a
 * single play for the floating reaction. Falls back to the raw emoji until the
 * JSON arrives (and if it ever fails), so an emote is never blank.
 */
export function LottieEmote({
  reaction,
  size,
  loop = false,
}: {
  reaction: string;
  size: number | string;
  loop?: boolean;
}) {
  const host = useRef<HTMLSpanElement>(null);
  const animRef = useRef<AnimationItem | null>(null);
  const file = EMOTE_FILE[reaction as (typeof REACTIONS)[number]];

  useEffect(() => {
    if (!file) return;
    let cancelled = false;
    void Promise.all([getLottie(), loadData(file)]).then(([lottie, animationData]) => {
      if (cancelled || !host.current) return;
      host.current.textContent = ''; // clear the emoji fallback
      animRef.current = lottie.loadAnimation({
        container: host.current,
        renderer: 'svg',
        loop,
        autoplay: true,
        animationData: structuredClone(animationData),
      });
    });
    return () => {
      cancelled = true;
      animRef.current?.destroy();
      animRef.current = null;
    };
  }, [file, loop]);

  return (
    <span
      ref={host}
      aria-hidden
      style={{ width: size, height: size, display: 'inline-block', lineHeight: 1, fontSize: typeof size === 'number' ? size * 0.9 : size }}
    >
      {/* Fallback emoji, shown until (or if) the Lottie loads. */}
      {file ? reaction : reaction}
    </span>
  );
}
